import { Vocation } from '@game/protocol';

/**
 * Paleta do jogo (PRD §8: "contorno limpo e paleta saturada").
 *
 * As cores ficam num arquivo só, e não espalhadas pelos desenhos, porque identidade
 * visual é decisão de arte: mudar o tom de roxo do Sorcerer deve ser uma linha, não uma
 * caçada por literais hexadecimais em cinco arquivos.
 *
 * ## A regra de cor do jogo: sombra fria, luz quente
 *
 * A versão anterior produzia sombra escurecendo a cor base e luz clareando a mesma cor —
 * e o resultado tinha a leitura de plástico, porque nenhuma superfície do mundo real (nem
 * do mundo desenhado) se comporta assim. Numa cena iluminada pelo sol, o que ilumina é
 * quente e o que sobra é o **céu**, que é frio: a sombra puxa para o violeta-azul, o
 * realce puxa para o amarelo. É a diferença entre "a mesma cor mais escura" e "a mesma
 * matéria, na sombra" — e é o que separa arte chapada de arte com atmosfera.
 *
 * `SHADOW_TINT` e `LIGHT_TINT` são os dois destinos dessa mistura, e `celShadow`/`celLight`
 * em `canvas.ts` são o caminho até eles. Todo desenho do jogo passa por lá, o que garante
 * que grama, pedra, roupa e escama sejam iluminadas pelo **mesmo sol**.
 */

export interface VocationPalette {
  /** Cor principal da roupa. É o que identifica a vocação de relance. */
  readonly cloth: string;
  /** Sombra da roupa, usada na metade inferior e no lado oposto à luz. */
  readonly clothShade: string;
  /** Pano batido pelo sol: o alto do ombro e a dobra que sobe. */
  readonly clothLight: string;
  /** Detalhe: cinto, ombreira, barra do manto. Muda com a faixa de equipamento. */
  readonly trim: readonly [string, string, string];
  readonly hair: string;
  readonly hairShade: string;
  /**
   * A faixa de brilho do cabelo.
   *
   * É o traço mais reconhecível do desenho japonês, e o mais barato: uma banda clara de
   * borda dura atravessando a franja diz "isto é cabelo de anime" antes de qualquer outro
   * detalhe do rosto ser legível.
   */
  readonly hairShine: string;
  /** Íris. Dois pixels, mas é o que dá vocação ao rosto de perto. */
  readonly eye: string;
  /** Cor da arma característica. */
  readonly weapon: string;
}

export const SKIN = '#ffd7ae';
export const SKIN_SHADE = '#e0a582';
export const EYE = '#2a2136';
export const EYE_LIGHT = '#ffffff';

/**
 * Contorno.
 *
 * Escuro, mas **violeta**, e não preto nem azul-chumbo. Preto absoluto sobre fundo escuro
 * apaga a silhueta, que é justamente o que precisa ficar legível a 100% de zoom; e o
 * cinza-azulado da versão anterior dava a tudo um contorno de esquadria técnica. Um roxo
 * muito escuro mantém o contraste e concorda com a sombra fria do resto da arte — o
 * contorno passa a ser a parte mais funda da sombra, e não uma linha alheia por cima do
 * desenho.
 */
export const OUTLINE = '#241d30';

/** Destino da sombra: o azul-violeta do céu, que é o que sobra onde o sol não bate. */
export const SHADOW_TINT = '#3b3a72';
/** Destino do realce: a luz do sol, quente e levemente amarela. */
export const LIGHT_TINT = '#fff3d2';

export const VOCATION_PALETTES: Readonly<Record<Vocation, VocationPalette>> = {
  [Vocation.Knight]: {
    cloth: '#a3b6d4',
    clothShade: '#586c93',
    clothLight: '#d8e6f7',
    trim: ['#8a7550', '#c9a04a', '#f2d67a'],
    hair: '#4d3527',
    hairShade: '#2c1d15',
    hairShine: '#8a674a',
    eye: '#4a7ad8',
    weapon: '#e6eef8',
  },
  [Vocation.Paladin]: {
    cloth: '#71b95d',
    clothShade: '#3f7b45',
    clothLight: '#a9dd8c',
    trim: ['#8a6440', '#c08847', '#eaba60'],
    hair: '#ecc744',
    hairShade: '#a5801b',
    hairShine: '#fff2a8',
    eye: '#46a05c',
    weapon: '#9c6539',
  },
  [Vocation.Druid]: {
    cloth: '#4fbb9b',
    clothShade: '#2c7d69',
    clothLight: '#93e6c8',
    trim: ['#6d6440', '#a0c85f', '#dbf28e'],
    hair: '#7f5b2e',
    hairShade: '#4f3719',
    hairShine: '#b98f52',
    eye: '#3fb098',
    weapon: '#82ac57',
  },
  [Vocation.Sorcerer]: {
    cloth: '#836ade',
    clothShade: '#4b3993',
    clothLight: '#bcaaff',
    trim: ['#4f4284', '#9382e6', '#d3c4ff'],
    hair: '#39344f',
    hairShade: '#211d31',
    hairShine: '#68628f',
    eye: '#a074f5',
    weapon: '#ab94f5',
  },
};

/**
 * Cores e proporções das criaturas e dos NPCs, indexadas pelo `outfitId` do template.
 *
 * Cada espécie tem desenho próprio (ver `art/creatures.ts`); o que mora aqui é o que
 * distingue uma da outra sem mexer no traço — a paleta e o tamanho.
 *
 * ## Por que cinco tons, e não três
 *
 * A primeira versão tinha base, sombra e luz. Três tons desenham um recorte colorido: dá
 * para dizer onde o bicho está, não do que ele é feito. O que lê como matéria é a
 * **transição** — e ela precisa de um tom a mais em cada ponta: um escuro de contato, que
 * é onde a barriga encontra o chão e onde uma perna passa atrás da outra, e um claro de
 * borda, que é a luz raspando o dorso. Com cinco, o mesmo desenho passa a ter volume; com
 * três, ele continua sendo uma silhueta preenchida por mais que se ajuste o meio.
 *
 * Os NPCs de conversa entram na mesma tabela porque o cliente os desenha pelo mesmo
 * caminho — `EntityKind.Npc` com `outfitId` — e uma segunda tabela só para eles criaria
 * duas fontes para a mesma pergunta: "que aparência tem o outfit 3?".
 */
export interface CreaturePalette {
  /** Contato e vãos: onde a barriga encontra o chão, onde uma perna passa atrás da outra. */
  readonly deep: string;
  readonly shade: string;
  readonly body: string;
  /** Dorso batido pela luz, que vem de cima e da esquerda em toda a arte do jogo. */
  readonly light: string;
  /** Borda iluminada, um pixel no alto. É o que separa o bicho do fundo escuro. */
  readonly highlight: string;
  /** Focinho, orelha, presa, avental: a cor que não é pele nem pelo. */
  readonly detail: string;
  /** Segunda cor de identidade: interior da orelha no animal, cabelo no NPC. */
  readonly accent: string;
  readonly eye: string;
  /**
   * Altura do corpo dentro do quadro do painter — 32 no comum, 48 no alto.
   *
   * Teto de 28 no quadro comum: com o chão em 29, o que passar disso é desenhado fora do
   * quadro e volta decapitado — que era o que acontecia com o Troll em 30.
   *
   * É **proporção**, e não tamanho no mundo: quanto do quadro o corpo ocupa, e portanto
   * quão esguio ou atarracado ele é ao lado das próprias pernas. O tamanho na tela é outra
   * coisa e mora em `Outfit.scale`, em `art/creatures.ts` — mexer aqui para "aumentar o
   * bicho" só estica as pernas dele, porque as larguras do painter são fixas.
   */
  readonly height: number;
}

export const CREATURE_PALETTES: readonly CreaturePalette[] = [
  // 0 — Rato: marrom acinzentado, orelha e cauda rosadas.
  {
    deep: '#2e2721',
    shade: '#4a4139',
    body: '#6f6357',
    light: '#8f8275',
    highlight: '#b0a496',
    detail: '#d9a99a',
    accent: '#c07a6a',
    eye: '#c8443a',
    height: 15,
  },
  // 1 — Lobo: cinza frio, peito e juba claros, focinho escuro.
  {
    deep: '#22262b',
    shade: '#3a4048',
    body: '#5e6570',
    light: '#868e99',
    highlight: '#c2c9d2',
    detail: '#d8dee6',
    accent: '#2b2f35',
    eye: '#e8c84a',
    height: 21,
  },
  // 2 — Troll: verde de mato, presas de osso, verrugas mais escuras que a pele.
  {
    deep: '#26331a',
    shade: '#41522b',
    body: '#6d8446',
    light: '#8fa864',
    highlight: '#b9cf8c',
    detail: '#d8c9a0',
    accent: '#54662f',
    eye: '#f2e58a',
    height: 27,
  },
  // 3 — Mercador: túnica marrom sob avental de couro.
  {
    deep: '#33241a',
    shade: '#513a24',
    body: '#7d5c39',
    light: '#9a7549',
    highlight: '#bb9060',
    detail: '#c8a86a',
    accent: '#3d2a1d',
    eye: '#20303f',
    // Altura de gente: a mesma do personagem jogável, medida no quadro alto de 48.
    height: 40,
  },
  // 4 — Banqueira: manto azul-noite com galão dourado.
  {
    deep: '#161d2d',
    shade: '#26304a',
    body: '#3c4a6d',
    light: '#55658c',
    highlight: '#8290b3',
    detail: '#d8b44a',
    accent: '#6b4a2a',
    eye: '#20303f',
    height: 40,
  },
  // --- O bestiário, uma paleta por espécie ---
  //
  // A versão anterior tinha uma paleta por **família**, e vinte e cinco espécies dividiam
  // cinco cores: o Urso saía com a pelagem cinza do Lobo e o Lobo negro saía igual ao Lobo
  // comum — o nome prometia uma aparência que o atlas não entregava. A paleta é barata de
  // escrever e é metade da identidade de uma espécie; a outra metade, a silhueta, mora em
  // `creatures.ts`, e as duas listas andam alinhadas pelo mesmo índice.

  // 5 — Morcego: couro escuro, membrana avermelhada.
  {
    deep: '#1a1418',
    shade: '#2e2430',
    body: '#463a4a',
    light: '#635469',
    highlight: '#8b7a92',
    detail: '#a4586a',
    accent: '#241c26',
    eye: '#e8b34a',
    height: 12,
  },
  // 6 — Aranha: quitina fria com faixa de aviso.
  {
    deep: '#161a20',
    shade: '#232c36',
    body: '#36434f',
    light: '#4d5f6e',
    highlight: '#75899a',
    detail: '#c8a83c',
    accent: '#101318',
    eye: '#d8443a',
    height: 14,
  },
  // 7 — Esqueleto: osso e mortalha, sem nada quente.
  {
    deep: '#2a2a26',
    shade: '#46463f',
    body: '#6e6d62',
    light: '#93917f',
    highlight: '#c9c6ae',
    detail: '#e4e0c6',
    accent: '#1c1c1a',
    eye: '#7fe0d0',
    height: 30,
  },
  // 8 — Elemental de terra: rocha fria com juntas de âmbar.
  {
    deep: '#1d2430',
    shade: '#2e3c4e',
    body: '#455a72',
    light: '#5f7c9a',
    highlight: '#8fb0cc',
    detail: '#e0a13c',
    accent: '#141a22',
    eye: '#ffe08a',
    height: 28,
  },
  // 9 — Lagarto: escama verde-oliva com ventre claro, rente ao chão.
  {
    deep: '#1d2a1c',
    shade: '#31462d',
    body: '#4c6a41',
    light: '#688a59',
    highlight: '#9bb583',
    detail: '#d0c48a',
    accent: '#22301f',
    eye: '#e8a33a',
    height: 13,
  },
  // 10 — Rato de caverna: pálido e azulado, de quem nunca viu sol; o olho é leitoso.
  {
    deep: '#2c3138',
    shade: '#47505b',
    body: '#6b7684',
    light: '#8d99a8',
    highlight: '#b5c1cf',
    detail: '#d9c9c2',
    accent: '#a88f9a',
    eye: '#d8d3c8',
    height: 14,
  },
  // 11 — Rato gigante: marrom fundo, mais alto nas patas que o rato comum.
  {
    deep: '#241d16',
    shade: '#3d3226',
    body: '#5c4c38',
    light: '#7a664b',
    highlight: '#9c8763',
    detail: '#caa08e',
    accent: '#a86a58',
    eye: '#d84a3a',
    height: 17,
  },
  // 12 — Javali: terra e cerda, focinho claro de fuçar.
  {
    deep: '#2a221a',
    shade: '#453828',
    body: '#64523a',
    light: '#82694a',
    highlight: '#a08862',
    detail: '#caa787',
    accent: '#3a2e20',
    eye: '#d8b04a',
    height: 17,
  },
  // 13 — Urso: pardo profundo, focinho de mel, orelha redonda.
  {
    deep: '#241a12',
    shade: '#3e2d1e',
    body: '#5d452c',
    light: '#7a5c3a',
    highlight: '#9a7a4e',
    detail: '#c9a87c',
    accent: '#8a6a45',
    eye: '#e0c05a',
    height: 22,
  },
  // 14 — Lobo negro: quase preto, e o olho vermelho é o único ponto quente.
  {
    deep: '#101216',
    shade: '#1d2128',
    body: '#2f353e',
    light: '#464e59',
    highlight: '#6a7480',
    detail: '#8b95a2',
    accent: '#14171c',
    eye: '#e04438',
    height: 22,
  },
  // 15 — Troll batedor: oliva escura de mato fechado, mais baixo que o troll da ponte.
  {
    deep: '#1d2814',
    shade: '#334422',
    body: '#526b38',
    light: '#6f8c4e',
    highlight: '#97b370',
    detail: '#cdbf96',
    accent: '#405426',
    eye: '#e8e08a',
    height: 25,
  },
  // 16 — Ogro: pele parda, pança clara, presa de osso. Quadro alto de 48.
  {
    deep: '#33261a',
    shade: '#52402a',
    body: '#7a6142',
    light: '#9a7d57',
    highlight: '#bfa176',
    detail: '#d9c9a0',
    accent: '#5c4930',
    eye: '#e8c04a',
    height: 34,
  },
  // 17 — Ciclope: pedra e pele acinzentada em volta de um olho só. Quadro alto de 48.
  {
    deep: '#2e2a26',
    shade: '#4b4740',
    body: '#6f6a60',
    light: '#908a7d',
    highlight: '#b5af9e',
    detail: '#d8cfb8',
    accent: '#4a443c',
    eye: '#e8b83a',
    height: 38,
  },
  // 18 — Escorpião: quitina âmbar queimada, juntas claras.
  {
    deep: '#33180e',
    shade: '#542a16',
    body: '#7c4322',
    light: '#9c5c30',
    highlight: '#c07d45',
    detail: '#e0b060',
    accent: '#2c150c',
    eye: '#f2d270',
    height: 13,
  },
  // 19 — Aranha gigante: preto-arroxeado com a marca clara no abdome.
  {
    deep: '#16121c',
    shade: '#262032',
    body: '#3a3350',
    light: '#544a6e',
    highlight: '#7d7398',
    detail: '#d8d0e8',
    accent: '#0f0c14',
    eye: '#e04438',
    height: 18,
  },
  // 20 — Vespa gigante: o aviso amarelo-e-preto; o detalhe é a listra, não um enfeite.
  {
    deep: '#251c0c',
    shade: '#4a3a14',
    body: '#b08820',
    light: '#d8b23a',
    highlight: '#f2d666',
    detail: '#1c1710',
    accent: '#5c4712',
    eye: '#f2e08a',
    height: 16,
  },
  // 21 — Carniçal: verde pútrido, garras de osso sujo. Quadro alto de 48.
  {
    deep: '#1e281c',
    shade: '#35462f',
    body: '#567048',
    light: '#748f60',
    highlight: '#9cb383',
    detail: '#d8d2b0',
    accent: '#42552f',
    eye: '#e8e04a',
    height: 30,
  },
  // 22 — Espectro: azul de névoa, quase sem corpo. Quadro alto de 48.
  {
    deep: '#1a2230',
    shade: '#2c3a50',
    body: '#465a74',
    light: '#66809c',
    highlight: '#96b3cc',
    detail: '#cfe6f5',
    accent: '#141b26',
    eye: '#8ff2e0',
    height: 32,
  },
  // 23 — Lich: manto roxo sobre osso, coroa de ouro morto. Quadro alto de 48.
  {
    deep: '#201a2c',
    shade: '#372e4c',
    body: '#544870',
    light: '#746592',
    highlight: '#a396c2',
    detail: '#e4e0c6',
    accent: '#d8b44a',
    eye: '#7fe0d0',
    height: 36,
  },
  // 24 — Elemental de fogo: do vermelho de brasa ao núcleo amarelo. Quadro alto de 48.
  {
    deep: '#451408',
    shade: '#7a2410',
    body: '#b8431a',
    light: '#e07226',
    highlight: '#f2a83c',
    detail: '#ffd966',
    accent: '#2e0d05',
    eye: '#fff2b0',
    height: 30,
  },
  // 25 — Elemental de gelo: azul de geleira com arestas quase brancas. Quadro alto de 48.
  {
    deep: '#1c3246',
    shade: '#2e506e',
    body: '#4a7ba0',
    light: '#74a8cc',
    highlight: '#a8d3ec',
    detail: '#e8f6ff',
    accent: '#163040',
    eye: '#d8f2ff',
    height: 30,
  },
  // 26 — Elemental de energia: violeta elétrico, arcos mais claros que o corpo. Quadro alto.
  {
    deep: '#241640',
    shade: '#3c2668',
    body: '#5c3aa0',
    light: '#7f5ccc',
    highlight: '#a98ae8',
    detail: '#d8c2ff',
    accent: '#180e2c',
    eye: '#f2e8ff',
    height: 30,
  },
  // 27 — Serpente: verde de junco com a barriga em creme.
  {
    deep: '#14260f',
    shade: '#26411c',
    body: '#3f612c',
    light: '#5a823e',
    highlight: '#82a85c',
    detail: '#d0d89a',
    accent: '#1d3015',
    eye: '#e8b83a',
    height: 14,
  },
  // 28 — Draconato: escama verde com a crista vermelha de sangue quente. Quadro alto de 48.
  {
    deep: '#232e14',
    shade: '#3c4d22',
    body: '#5d7434',
    light: '#7d9a48',
    highlight: '#a8c268',
    detail: '#d8503c',
    accent: '#46592a',
    eye: '#e8a33a',
    height: 36,
  },
  // 29 — Dragão: ferrugem e brasa, ventre e chifre em ouro velho.
  {
    deep: '#26140f',
    shade: '#451f14',
    body: '#6f3420',
    light: '#94502c',
    highlight: '#bd7440',
    detail: '#e8c860',
    accent: '#351a10',
    eye: '#f2d24a',
    height: 26,
  },

  // --- Os onze ofícios da cidade (PRD §15.8) ---------------------------------
  //
  // Sete NPCs da vila dividiam **dois** desenhos: o Mercador (3) e a Banqueira (4). Armeiro,
  // taverneiro, granjeiro e barqueiro eram literalmente o Bruno de pé em outro prédio, e a
  // Boticária era a Alda. A cidade inteira tinha duas caras, e a consequência é a que §15.8
  // descreve para o mapa e vale igual para a gente: "tudo diferente e nada memorável" — só
  // que aqui era o contrário, tudo igual e nada reconhecível.
  //
  // Cada um recebe agora um índice, e a **cor é metade da identidade**: quem trabalha com
  // ferro é cinza-ferrugem, quem trabalha com erva é verde-garrafa, quem trabalha com couro é
  // castanho. A outra metade é a silhueta, e mora nos painters de `art/creatures.ts`.
  //
  // As alturas ficam todas em 40, que é a altura de gente do quadro alto de 48: um lojista
  // mais baixo que o outro leria como criança ou como anão, e nenhuma das duas coisas é o que
  // a diferença entre um armeiro e um taverneiro deveria dizer.

  // 30 — Armeiro: cinza de forja com o brasido do avental de couro queimado.
  {
    deep: '#1d2024',
    shade: '#33383f',
    body: '#4c545e',
    light: '#6b7480',
    highlight: '#98a2ae',
    detail: '#8c5a2e',
    accent: '#2a2018',
    eye: '#20303f',
    height: 40,
  },
  // 31 — Boticária: manto verde-garrafa, gola e frasco em vidro claro.
  {
    deep: '#101f18',
    shade: '#1d3628',
    body: '#2d5340',
    light: '#417158',
    highlight: '#6f9f83',
    detail: '#c8e8d0',
    accent: '#5c3a22',
    eye: '#20303f',
    height: 40,
  },
  // 32 — Taverneiro: túnica vinho sob avental branco de cozinha.
  {
    deep: '#2a1418',
    shade: '#452026',
    body: '#6b3038',
    light: '#8c454c',
    highlight: '#b06e72',
    detail: '#e8dcc8',
    accent: '#3a2a1c',
    eye: '#20303f',
    height: 40,
  },
  // 33 — Granjeiro: linho cru e o chapéu de palha, que é a silhueta dele.
  {
    deep: '#3a3020',
    shade: '#5c4c30',
    body: '#8a7448',
    light: '#a89060',
    highlight: '#cbb488',
    detail: '#d8c072',
    accent: '#4a3a1e',
    eye: '#20303f',
    height: 40,
  },
  // 34 — Barqueiro: azul de lona molhada, gorro e remo em madeira clara.
  {
    deep: '#12222c',
    shade: '#1f3a4a',
    body: '#2f5a70',
    light: '#457a92',
    highlight: '#78a8bd',
    detail: '#c9a86a',
    accent: '#1a2a30',
    eye: '#20303f',
    height: 40,
  },
  // 35 — Curtidor: castanho de couro cru, avental mais claro que a túnica.
  {
    deep: '#2a1c10',
    shade: '#472e1a',
    body: '#6d4826',
    light: '#8d6236',
    highlight: '#b48a58',
    detail: '#c8a173',
    accent: '#33240f',
    eye: '#20303f',
    height: 40,
  },
  // 36 — Joalheira: manto ameixa com galão de ouro. Único dourado da cidade fora do banco.
  {
    deep: '#1e1024',
    shade: '#331a3c',
    body: '#502a5c',
    light: '#6d3f7c',
    highlight: '#9a6fa8',
    detail: '#e8c45e',
    accent: '#2a1618',
    eye: '#20303f',
    height: 40,
  },
  // 37 — Sacerdote: manto de linho tinto de ocre, com o sol do Alvorecer no peito.
  //
  // A primeira versão era linho **claro**, e a tela mostrou o problema: ele fica de pé no
  // templo, e o piso do templo é o mais claro do jogo — a zona de proteção é clareada de
  // propósito, para a fronteira da segurança se ler de relance (§8.4). Um manto claro sobre
  // piso claro apagava a silhueta inteira, e o que sobrava visível era o sol dourado do peito
  // flutuando sozinho. O ocre é dois degraus mais escuro que a laje consagrada, e é isso que
  // devolve a figura.
  {
    deep: '#2c2418',
    shade: '#473c28',
    body: '#6b5c40',
    light: '#877553',
    highlight: '#ab9974',
    detail: '#f0c85e',
    accent: '#59492f',
    eye: '#20303f',
    height: 40,
  },
  // 38 — Guarda: aço azulado sobre gambeson, lança de madeira escura.
  {
    deep: '#161c26',
    shade: '#28323f',
    body: '#3e4a5c',
    light: '#5b6a80',
    highlight: '#8f9db3',
    detail: '#b7bec7',
    accent: '#2a1f18',
    eye: '#20303f',
    height: 40,
  },
  // 39 — Aldeão: lã cor de terra. A cor mais apagada da cidade, e é o ponto: quem não tem
  // ofício não tem uniforme, e é contra ele que os dez uniformes se leem.
  {
    deep: '#2e2a22',
    shade: '#4a4436',
    body: '#6e6550',
    light: '#8b8168',
    highlight: '#b0a68a',
    detail: '#c8bda0',
    accent: '#463424',
    eye: '#20303f',
    height: 40,
  },
  // 40 — Galinha: penugem creme, crista e barbela vermelhas, bico e pata de milho.
  {
    deep: '#5c4a2a',
    shade: '#a8905c',
    body: '#e0cf9e',
    light: '#f0e4c0',
    highlight: '#fdf6e2',
    detail: '#e8a83a',
    accent: '#c8402e',
    eye: '#2a1a14',
    height: 12,
  },
];

export function creaturePalette(outfitId: number): CreaturePalette {
  return CREATURE_PALETTES[outfitId] ?? CREATURE_PALETTES[0]!;
}

/**
 * Terreno.
 *
 * Cada material tem uma **rampa** de tons, do escuro ao claro, e o ruído por pixel sorteia
 * dentro dela. É a gradação — não o desenho — que faz o chão parecer matéria em vez de
 * retângulo pintado.
 *
 * As rampas são curtas e de contraste baixo de propósito. A versão anterior espalhava
 * cinco verdes muito separados por pixels isolados, e o resultado lia como confete: o
 * olho via o ruído antes de ver o gramado. Tons vizinhos em blocos de 2×2 dão a mesma
 * sensação de matéria sem competir com criaturas e itens, que são o que precisa saltar.
 */
export const TILE_PALETTE = {
  // --- Grama ---
  // Verde puxado para o amarelo, e não para o azul. Campo de anime é ensolarado: o verde
  // frio da versão anterior lia como musgo de sombra, e um mapa inteiro dele dá ao mundo
  // um clima de porão que nenhuma iluminação por cima conserta.
  grassRamp: ['#4e8140', '#578e46', '#619b4c', '#6ba854'] as readonly string[],
  grassPatch: '#3d6b34',
  grassBlade: '#74b458',
  // A ponta da lâmina é clara, não branca. Com `#a8e07e` — dois degraus acima do topo da
  // rampa — cada tufo virava um respingo brilhante, e três tufos por tile num campo
  // inteiro leem como chuvisco, não como capim.
  grassBladeLight: '#93cf6a',
  flowerPetal: '#ffe071',
  flowerAlt: '#ff9fb4',
  flowerLeaf: '#5f9648',

  // --- Terra ---
  dirtRamp: ['#7a5f42', '#846849', '#8f7251', '#9a7c59'] as readonly string[],
  dirtPatch: '#66503a',
  dirtStone: '#b0a189',
  dirtStoneShade: '#7d6e59',

  // --- Areia ---
  sandRamp: ['#ccb47f', '#d5be89', '#dec894', '#e7d29f'] as readonly string[],
  sandGrain: '#b89f6d',
  sandShell: '#fff2da',

  // --- Calçamento ---
  // Cinza puxado para o quente: pedra de calçada tem areia na composição, e um cinza
  // neutro ao lado da grama lê como concreto de estacionamento.
  paveRamp: ['#9a9284', '#a49b8c', '#ada395', '#b7ac9d'] as readonly string[],
  paveJoint: '#5f594f',
  paveHighlight: '#d4c9b6',
  paveCrack: '#544f47',

  // --- Piso consagrado (zona de proteção) ---
  // Azul frio sobre a pedra quente do calçamento. O contraste é de temperatura, e não de
  // valor: no meio de um mapa em que tudo é verde, marrom e cinza, o frio é a única
  // família de cor que ainda não foi usada pelo chão — então a zona se distingue mesmo
  // vista pela beira da tela, sem precisar ser mais clara que tudo o resto.
  sanctuaryVein: '#8fd0f5',
  sanctuaryGlow: '#dcf2ff',

  // --- Rocha do subsolo ---
  caveRamp: ['#514c45', '#59544c', '#615c53', '#69645a'] as readonly string[],
  caveCrack: '#37342f',
  caveGrit: '#8a8377',

  // --- Afloramento de rocha ---
  wallBase: '#8e8474',
  wallTop: '#b4a894',
  wallLight: '#dbcdb2',
  wallDark: '#544c42',
  wallDeep: '#332d27',
  wallMoss: '#6c8a4e',

  // --- Alvenaria da cidade ---
  //
  // Tinta própria, e não a do afloramento. As duas dividiam `wallBase` — e no meio da cidade
  // isso pôs a **parede** e o **calçamento** na mesma faixa de valor: a pedra da alvenaria
  // saía entre `#87` e `#b0`, a lajota do chão entre `#9a` e `#b7`, e as duas são bloco de
  // canto redondo em fiada. O resultado é o que se via na tela — não dava para dizer onde a
  // praça acabava e o muro começava, e um cenário em que o jogador não distingue o que
  // atravessa do que não atravessa falhou antes de qualquer regra de colisão.
  //
  // A separação é de **valor**, porque é o que sobrevive a um zoom de 1× e a uma tela ruim:
  // o corpo da alvenaria fica um terço mais escuro que a lajota mais escura do chão, sem
  // faixa de sobreposição. O afloramento continua claro porque o problema dele é o oposto —
  // ele vive dentro da caverna, contra um piso que já é escuro (ver `caveRamp`), e escurecê-lo
  // junto apagaria a parede da galeria.
  //
  // Um degrau **mais frio** por cima disso: a pedra da rua tem areia na composição e puxa
  // para o quente, e a alvenaria cortada em pedreira não. É contraste de sobra numa cor só.
  masonryBase: '#5f5a52',
  /** Topo à vista: o coroamento. Claro o bastante para ler como quina iluminada. */
  masonryTop: '#93897a',
  masonryLight: '#bdb096',

  // --- Água ---
  // A rampa é curta de propósito. Com tons muito separados, o ruído de 2×2 que dá
  // matéria ao chão vira chuvisco de televisão na água: o olho lê ruído, não superfície.
  // Aqui a variação de fundo é quase imperceptível e quem carrega o movimento são as
  // marolas e os brilhos, que é como água parada de fato se comporta.
  waterDeep: '#12455f',
  waterBase: '#175372',
  waterMid: '#1d6084',
  waterLight: '#4295bc',
  waterSparkle: '#a2dff7',
  waterFoam: '#e4f5ff',

  // --- Vegetação alta ---
  //
  // Cinco degraus de folha, e não três: a copa é desenhada em **bandas de borda dura**
  // (ver `drawTree`), e uma banda a menos deixa a transição do escuro ao claro larga
  // demais para ler como recorte. É a mesma economia da paleta de criatura — o que dá
  // matéria é a transição, e transição precisa de degrau.
  trunk: '#6b4a2c',
  trunkShade: '#452e1a',
  leafDark: '#255022',
  leafBase: '#31682b',
  leafMid: '#3f8034',
  leafLight: '#549b41',
  leafHighlight: '#7cc257',
  /** Luz raspando o alto da copa. É o que separa a árvore do que estiver atrás dela. */
  leafRim: '#aee283',
  pineDark: '#1c3f2a',
  pineBase: '#265232',
  pineLight: '#37703c',
  pineRim: '#63a85c',

  // --- Pedra solta ---
  rockBase: '#97897a',
  rockLight: '#c2b4a2',
  rockShade: '#6a6156',
  rockDeep: '#443e37',

  // Violeta: nenhum outro terreno usa esta faixa, então o portal se lê como "isto não é
  // parte do cenário" mesmo no canto do olho.
  portalGlow: '#dcb8ff',
  portalCore: '#8a52e0',

  // --- O subsolo (PRD §15.6) ---
  //
  // A caverna tinha **dois** tons no mundo inteiro: o cinza da rocha lisa e o mesmo cinza
  // da parede. Oito andares assim são oito vezes a mesma fotografia, e nenhuma quantidade
  // de planta conserta isso — planta dá estrutura, cor dá lugar.
  //
  // Cada família abaixo é uma faixa de cor que **não** aparece em mais nada do jogo, e é
  // essa exclusividade que faz uma sala se anunciar antes de o jogador entrar nela: laranja
  // é caldeira, ciano é cristal, e osso é cripta.

  /** Lava: a única fonte de cor quente do subsolo. Rampa curta, brilho por cima. */
  lavaDeep: '#5c1606',
  lavaBase: '#a5300b',
  lavaMid: '#d9530f',
  lavaHot: '#f79426',
  lavaGlow: '#ffd47a',
  lavaCrust: '#3a1a12',

  /** Cristal: ciano frio, a única cor fria do subsolo depois da água. */
  crystalDeep: '#123f52',
  crystalBase: '#1f7f9c',
  crystalLight: '#5fc9d9',
  crystalGlow: '#c9f7ff',

  /** Cogumelo: chapéu de cor quente sobre pé pálido. */
  mushroomStem: '#d6cbb0',
  mushroomStemShade: '#a2967c',
  mushroomCap: '#9c3a4e',
  mushroomCapLight: '#c8596a',
  mushroomSpot: '#f0e0c8',

  /** Teia: quase branca, e translúcida na composição. */
  webThread: '#d8dce4',
  webShade: '#9aa2ad',

  /** Ossada: o material da cripta. Marfim envelhecido, nunca branco. */
  boneBase: '#cfc3a4',
  boneShade: '#9a8f74',
  boneDark: '#6b6350',

  /** Minério na rocha: dourado sujo, com um brilho por cristal. */
  oreBase: '#b98a2e',
  oreLight: '#f0c85e',
  oreDark: '#6d4f16',

  /** Madeira: porta, baú e cabo de alavanca. */
  woodBase: '#8a5a2b',
  woodLight: '#b98046',
  woodDark: '#5a3618',
  /** Ferragem: dobradiça, fechadura, aro de baú, haste de alavanca. */
  ironBase: '#7d838c',
  ironLight: '#b7bec7',
  ironDark: '#3f444b',
  /** Latão da fechadura e do cadeado: separa a porta trancada da fechada de relance. */
  brass: '#c8a33c',
  /** Fibra: a corda do arco, e o que mais for trançado. */
  ropeBase: '#a8895c',
  ropeLight: '#d6bb8c',

  /**
   * O barco do cais (PRD §15.8).
   *
   * Casco mais escuro que a madeira de porta e baú, e de propósito: madeira de embarcação é
   * calafetada com piche, e um casco cor de móvel boiando no azul do mar leria como caixote.
   * A vela é quase branca porque ela é o que se enxerga de longe — é ela que diz "cais" a
   * quem chega andando pela praia, antes de o barqueiro caber na tela.
   */
  boatHull: '#6b4526',
  boatHullDark: '#452a15',
  boatPlank: '#8f6236',
  boatTrim: '#c9a86a',
  boatSail: '#e8e2d0',
  boatSailShade: '#bdb59f',
  boatMast: '#7a5730',

  /**
   * A cidade (PRD §15.8).
   *
   * ## A calçada, e por que ela não é o calçamento com outro nome
   *
   * A rua e o piso de dentro de casa são o mesmo `Material` — misturam igual com a grama —, e
   * é o **desenho** que os separa. A laje de dentro é cortada em blocos grandes e claros; a
   * calçada é seixo miúdo assentado em rejunte escuro, um degrau mais escura que a laje. Duas
   * consequências, e as duas importam: a rua fica mais escura que o interior, o que faz a
   * porta iluminada ler como entrada, e o rejunte dá à via uma **direção** — as fileiras de
   * pedra acompanham o eixo da rua, e é por elas que o olho segue a esquina.
   *
   * ## A madeira da mobília não é a madeira da porta
   *
   * `woodBase` é madeira encerada de porta e baú. Balcão, cerca e caixaria são madeira de
   * trabalho: mais fria, mais cinzenta, com o veio à mostra. Reaproveitar a rampa da porta
   * faria a cidade inteira parecer feita do mesmo móvel, que é o defeito que a paleta do
   * subsolo já teve de consertar uma vez (ver o bloco de §15.6 acima).
   */
  cobbleRamp: ['#7f7a70', '#8a857a', '#948e83', '#9e978b'] as readonly string[],
  cobbleJoint: '#4c4842',
  cobbleLight: '#b6ac9c',

  timberBase: '#8e6b45',
  timberLight: '#b48f61',
  timberDark: '#5b4227',
  timberDeep: '#3a2a19',

  /** Lampião: o poste é ferro, e a luz é a única cor quente de uma rua de pedra. */
  lampGlass: '#ffe6a0',
  lampFlame: '#fff6d8',
  lampHalo: '#a8862e',
  lampPost: '#4a4a52',
  lampPostLight: '#787f8a',

  /** Placa: tábua clara com a letra escura. A letra é sugerida, não escrita — ver `drawSign`. */
  signBoard: '#c9a86a',
  signInk: '#4a3826',
} as const;
